import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import EdSpeedDial, {
	EdSpeedDialAction,
} from '@Components/UI/Utilities/EdSpeedDial/EdSpeedDial';
import { TableHeader } from '@Features/generics/generics.type';
import { useTypedSelector } from '@Features/store';
import { TableCell, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { testsReportsGetAll } from '../../Slices/TestsReports.slice';
import { TableCellStyled } from './TestsTable';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { FlexLayout } from '@Styled/utilities';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import styled from 'styled-components';
import { UsersRequester } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/Services/Userprogress/UserProgressRequester';
import { useSnackbar } from '@Providers/useSnackbar';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import ReportCards from './Components/ReportCards';
import {
	singleTestReportExportData,
	singleTestReportGetAll,
} from '../../Slices/SingleTestReport.slice';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import TestAttemptModal from './Components/TestAttemptModal';
import { CourseSingleTest } from '../../Types/TestReports.types';
import { fetchTestAttemptsDropdown } from '@Features/dropdowns/dropdownsSlice';
import { SingleTestReportRequester } from '../../Services/SingleTestReport.requester';
import NotificationRecipientCell from './Components/NotificationRecipientCell';
import usePlatformMode from '@Hooks/usePlatformMode';

const TABLE_HEADS: Array<TableHeader> = [
	{ displayName: 'UserName', fieldName: 'name', width: '13%' },
	{
		displayName: 'Name',
		// fieldName: 'first_name',
		// width: '11%',
	},

	{
		displayName: 'Phone',
		fieldName: 'phone',
		width: '12%',
	},
	{ displayName: 'Parent', fieldName: 'parent_phone', width: '11%' },
	{ displayName: '#Attempts', fieldName: 'attempts_count', width: '11%' },
	{ displayName: 'Date', fieldName: 'attempt_date', width: '13%' },
	{
		displayName: 'Score',
		fieldName: 'attempt_score',
		width: '12%',
	},
	{
		displayName: 'Notifications',
		width: '14%',
	},
];

const SingleTestTable = () => {
	const { id } = useParams<{ id: string }>();
	const { displaySnackbar } = useSnackbar();
	const [userId, setUserId] = useState<CourseSingleTest | undefined>();
	const [openResetModel, setOpenResetModel] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);

	const dispatch = useDispatch();

	const { fetchAll, filters, meta, query } = useTypedSelector(
		(state) => state.TestsReports
	);
	const { currentEntity } = useTypedSelector((state) => state.Tests);
	const { currentCourse } = useTypedSelector((state) => state.classroom);

	const getAllReports = () => {
		dispatch(
			testsReportsGetAll({
				page: meta?.current_page as number,
				perPage: meta?.per_page ?? 10,
				filters,
				sortBy: {},
				query: query as string,
				id: currentCourse?.id,
			})
		);
	};
	const getSingleTest = () => {
		dispatch(
			singleTestReportGetAll({
				page: 1,
				perPage: 10,
				filters: [],
				sortBy: {},
				query: '',
				id,
			})
		);
	};

	useEffect(() => {
		getAllReports();
	}, []);

	const {
		fetchAll: singleTest,
		filters: testFilter,
		meta: testMeta,
		selectedFilters,
		dateRange,
		query: testQuery,
		sortBy: testSortBy,
	} = useTypedSelector((state) => state.SingleTestReport);

	const reports = fetchAll.find((_) => _.id === Number(id));
	const [refetch, setRefetch] = useState(false);

	const onPageChangeRequest = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
				dispatch(
					singleTestReportGetAll({
						page: activePage,
						filters,
						perPage: perPage ?? 10,
						query,
						sortBy,
						from: startDate,
						to: endDate,
						id,
					})
				);
			setRefetch(false);
		},
		[]
	);

	const renderStatus = (status: string, evaluationStatus?: string) => {
		// amber for attempts still awaiting manual grading; neutral fallback so
		// an unknown status never renders white-on-white
		if (evaluationStatus === 'review') return '#ffab00';
		switch (status) {
			case 'passed':
				return '#00d66b';
			case 'failed':
				return '#ff3100';
			default:
				return '#a4a4a4';
		}
	};

	const handleModelResetClose = async (close: boolean, confirm?: boolean) => {
		if (confirm) {
			try {
				if (userId?.student_id) {
					UsersRequester.getInstance()
						.toggleUnits(Number(id), userId.student_id)
						.then(() => {
							getSingleTest();
							getAllReports();
						});
				}
				displaySnackbar('success', `Reset User successfully`);
			} catch (e) {
				const msg = e?.response?.data?.message || 'Something went wrong';
				displaySnackbar('error', msg);
			}
		}
		setOpenResetModel(false);
	};

	const attempts = useTypedSelector(
		(state) => state.dropdowns.dropdownTestAttempts
	);

	useEffect(() => {
		if (reports?.id && userId?.student_id) {
			dispatch(
				fetchTestAttemptsDropdown({
					unitId: reports?.id,
					userId: userId?.student_id,
				})
			);
		}
	}, [reports?.id, userId?.student_id]);

	// Per-recipient WhatsApp resend (user decision: SMS is one-shot — its
	// chips are status-only, no resend control). The backend resets the
	// outbox row to pending; the student backend's dispatcher re-resolves
	// the phone and template variables and sends on its next cycle. A
	// delivered/read message resent is a deliberate duplicate, hence the
	// confirmation. A chip renders when the test's notification options
	// tick that channel for that recipient OR the row already has history
	// (a past send stays visible whatever the ticks say now). Platform
	// flags play no part in visibility: a WhatsApp retry clicked while the
	// provider is off gets the backend's own 422 pointing at Settings.
	const { whatsappEnabled, smsEnabled } = usePlatformMode();
	const notificationsVisible = whatsappEnabled || smsEnabled;
	const tableHeads = useMemo(
		() =>
			notificationsVisible
				? TABLE_HEADS
				: TABLE_HEADS.filter((head) => head.displayName !== 'Notifications'),
		[notificationsVisible]
	);
	const [resendTarget, setResendTarget] = useState<{
		attemptId: number;
		recipient: 'student' | 'parent';
		duplicate: boolean;
	} | null>(null);
	const [resending, setResending] = useState(false);

	const performResend = async (attemptId: number, recipient: 'student' | 'parent') => {
		if (!currentCourse) return;
		setResending(true);
		try {
			await SingleTestReportRequester.getInstance().resendNotification({
				course_id: currentCourse.id,
				attempt_id: attemptId,
				recipient,
				channel: 'whatsapp',
			});
			displaySnackbar('success', `WhatsApp message to the ${recipient} re-queued`);
			setRefetch(true);
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Unable to resend the message'
			);
		} finally {
			setResending(false);
		}
	};

	const requestResend = (
		attemptId: number,
		recipient: 'student' | 'parent',
		status?: string | null
	) => {
		if (status === 'delivered' || status === 'read') {
			setResendTarget({ attemptId, recipient, duplicate: true });
		} else {
			performResend(attemptId, recipient);
		}
	};

	return (
		<>
			<EdConfirmModal
				open={!!resendTarget}
				text={`This WhatsApp message was already delivered. Send it again to the ${
					resendTarget?.recipient ?? ''
				}?`}
				handleClose={(close, confirm) => {
					const target = resendTarget;
					setResendTarget(null);
					if (confirm && target) performResend(target.attemptId, target.recipient);
				}}
			/>
			<EdConfirmModal
				text="Are you sure you want to reset this User?"
				open={openResetModel}
				handleClose={handleModelResetClose}
			/>
			<Name>{reports?.name}</Name>
			<ReportCards report={reports?.report} />
			<FlexLayout justifyContent="flex-end">
				<RoundButton
					onClick={() =>
						dispatch(
							singleTestReportExportData({
								page: testMeta?.current_page ?? 1,
								perPage: testMeta?.per_page ?? 10,
								filters: selectedFilters,
								query: testQuery ?? '',
								sortBy: testSortBy,
								from: dateRange.from,
								to: dateRange.to,
								id,
							})
						)
					}
					startIcon={<EdIcon>south</EdIcon>}
					variant="contained"
				>
					Export
				</RoundButton>
			</FlexLayout>
			{userId && (
				<TestAttemptModal
					handleModelClose={setShowModal}
					openModel={showModal}
					user={userId}
					testInfo={reports}
					attempts={attempts}
					setUserId={setUserId}
				/>
			)}
			<MainTable
				total={testMeta?.total ?? 0}
				dateFilter
				refetch={refetch}
				pageNum={testMeta?.current_page}
				tableFilters={testFilter}
				tableHeads={tableHeads}
				totalPages={testMeta?.last_page ?? 0}
				RowsperPage={Number(testMeta?.per_page)}
				$hasActions
				onPageChangeRequest={onPageChangeRequest}
				renderItems={() => {
					return singleTest?.length <= 0 ? (
						<MainTableRow>
							<TableCell colSpan={tableHeads.length}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						singleTest?.map((user, index) => {
							return (
								<MainTableRow
									onClick={() => {
										setUserId(user);
										setShowModal(true);
									}}
									key={user?.id}
									hover
								>
									<Tooltip title={user?.username} placement="top">
										<TableCellStyled>{user?.username}</TableCellStyled>
									</Tooltip>
									<Tooltip
										title={`${user?.first_name} ${user?.last_name}`}
										placement="top"
									>
										<TableCellStyled>
											{user?.first_name} {user?.last_name}
										</TableCellStyled>
									</Tooltip>

									<Tooltip title={user?.phone_number} placement="top">
										<TableCellStyled>{user?.phone_number}</TableCellStyled>
									</Tooltip>
									<Tooltip title={user?.parent_phone} placement="top">
										<TableCellStyled>{user?.parent_phone}</TableCellStyled>
									</Tooltip>
									<TableCellStyled>{user?.attempts_count}</TableCellStyled>

									{!user?.created_at ? (
										'-'
									) : (
										<Tooltip
											title={<DateTimeFormatter date={user?.created_at} />}
											placement="top"
										>
											<TableCellStyled>
												<DateTimeFormatter date={user?.created_at} />
											</TableCellStyled>
										</Tooltip>
									)}
									<TableCellStyled>
										<Status
											background={renderStatus(user?.status, user?.evaluation_status)}
										>
											{user?.evaluation_status === 'review'
												? 'Review'
												: `${user?.score} / ${user?.overall_score}`}
										</Status>
									</TableCellStyled>
									{notificationsVisible && (
										<NotificationsCell width={'13rem'}>
											<FlexLayout flexDirection="column" gridGap="0.25rem">
												<FlexLayout alignItems="center" gridGap="0.25rem">
													{((user.student_notification_options ?? '').includes('sms') ||
														user.sms_student_status) && (
														<NotificationRecipientCell
															who="Student"
															channel="sms"
															status={user.sms_student_status}
															errorCode={user.sms_student_error_code}
															error={user.sms_student_error}
															updatedAt={user.sms_student_updated_at}
														/>
													)}
													{((user.student_notification_options ?? '').includes('whatsapp') ||
														user.whatsapp_student_status) && (
															<NotificationRecipientCell
																who="Student"
																channel="whatsapp"
																status={user.whatsapp_student_status}
																errorCode={user.whatsapp_student_error_code}
																error={user.whatsapp_student_error}
																updatedAt={user.whatsapp_student_updated_at}
																resending={resending}
																onResend={
																	user.evaluation_status === 'review'
																		? undefined
																		: () =>
																				requestResend(
																					user.id,
																					'student',
																					user.whatsapp_student_status
																				)
																}
															/>
														)}
												</FlexLayout>
												<FlexLayout alignItems="center" gridGap="0.25rem">
													{((user.parent_notification_options ?? '').includes('sms') ||
														user.sms_parent_status) && (
														<NotificationRecipientCell
															who="Parent"
															channel="sms"
															status={user.sms_parent_status}
															errorCode={user.sms_parent_error_code}
															error={user.sms_parent_error}
															updatedAt={user.sms_parent_updated_at}
														/>
													)}
													{((user.parent_notification_options ?? '').includes('whatsapp') ||
														user.whatsapp_parent_status) && (
															<NotificationRecipientCell
																who="Parent"
																channel="whatsapp"
																status={user.whatsapp_parent_status}
																errorCode={user.whatsapp_parent_error_code}
																error={user.whatsapp_parent_error}
																updatedAt={user.whatsapp_parent_updated_at}
																resending={resending}
																onResend={
																	user.evaluation_status === 'review'
																		? undefined
																		: () =>
																				requestResend(
																					user.id,
																					'parent',
																					user.whatsapp_parent_status
																				)
																}
															/>
														)}
												</FlexLayout>
											</FlexLayout>
										</NotificationsCell>
									)}
									<TableCell>
										<EdSpeedDial ariaLabel="SpeedDial basic example">
											<EdSpeedDialAction
												key="reset test"
												icon={<RestartAltIcon color="action" />}
												tooltipTitle="Reset test"
												tooltipPlacement="left"
												onClick={(e) => {
													e.stopPropagation();
													setUserId(user);
													setOpenResetModel(true);
												}}
											/>
										</EdSpeedDial>
									</TableCell>
								</MainTableRow>
							);
						})
					);
				}}
			/>
		</>
	);
};

/**
 * The shared report cell clips with nowrap + hidden overflow — fine for
 * text, but it sliced the notification chips to slivers. Chips wrap instead.
 */
const NotificationsCell = styled(TableCellStyled)`
	white-space: normal;
	overflow: visible;
	text-overflow: unset;
`;

const Status = styled.p<{ background: string | undefined }>`
	background: ${({ background }) => background};
	height: 1.688rem;
	width: fit-content;
	font-size: 0.875rem;
	font-weight: bold;
	color: #fff;
	border-radius: 16px;
	padding: 0.25rem 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto;
`;
const Name = styled.p`
	color: #251b33;
	font-size: 1.375rem;
	font-weight: 600;
	margin-top: 1.531rem;
	margin-bottom: 1rem;
`;

export default SingleTestTable;
